import React, { Suspense } from 'react'
import { Heading, Icon, Button } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Loading } from '@pandiafi/components'

import { View, RoundLink } from '../../components'
import { useContractPaths } from '../../hooks/useContractPaths/useContractPaths'
import routePath from '../../router/routePath'
import { useCurrentContract } from '../../hooks/useCurrentContract/useCurrentContract'
import { TContact } from '../../../common/validations/contractContacts'
import { ContactCategory } from '../../../common/types'
import { ContactInfoSidebarDataLoader } from './ContactInfoSidebarDataLoader'

import { ContactInfoNoticesList } from 'components/ContactInfoNoticesList'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'

export const ContactInfoView = ({ contacts }: { contacts: TContact[] }) => {
  const { t } = useTranslation(['common', 'titles', 'contact'])
  const { frontPagePath } = useContractPaths()
  const navigate = useNavigate()
  const { currentContractId } = useCurrentContract()
  const modules = useModuleConfig()

  const {
    propertyManager,
    propertyMaintenance,
    propertySecretaries,
    customerService,
    salesRepresentative,
    residentsCommittee,
  } = resolveContacts(contacts)

  const hasDefectNotice =
    (modules.defectNotice.enabled &&
      !modules.defectNotice.customDefectNoticeLinkUrl) ??
    false
  const hasFeedbackOrQuestion =
    modules.defectNotice.feedback || modules.defectNotice.question

  return (
    <View>
      <View.Header>
        <View.HeaderLink to={frontPagePath}>
          {t('common:views.home')}
        </View.HeaderLink>
        <View.HeaderTitle>{t('contact:contactInformation')}</View.HeaderTitle>
      </View.Header>

      <View.Sidebar>
        {hasDefectNotice && hasFeedbackOrQuestion && (
          <Suspense fallback={<Loading variant="details" />}>
            <ContactInfoSidebarDataLoader>
              {({ feedbackAndQuestionNotices }) => (
                <ContactInfoNoticesList notices={feedbackAndQuestionNotices} />
              )}
            </ContactInfoSidebarDataLoader>
          </Suspense>
        )}
      </View.Sidebar>
      <View.Content>
        <div>
          {propertyMaintenance && (
            <ContactBlock
              title={propertyMaintenance.officeName}
              contact={propertyMaintenance}
              label={t('contact:propertyMaintenance')}
            />
          )}

          {propertyManager && (
            <ContactBlock
              title={propertyManager.officeName}
              contact={propertyManager}
              label={t('contact:propertyManager')}
            />
          )}

          {/* Property company is already shown in the property manager's title */}
          {propertySecretaries?.map((propertySecretary) => (
            <ContactBlock
              key={propertySecretary.id}
              title={propertyManager ? undefined : propertySecretary.officeName}
              contact={propertySecretary}
              label={t('contact:propertySecretary')}
            />
          ))}

          {customerService && (
            <ContactBlock
              title={t('contact:customerService')}
              contact={customerService}
              showName={false}
            />
          )}

          {hasDefectNotice && hasFeedbackOrQuestion && (
            <div className="mb-8 flex flex-col sm:flex-row gap-4">
              {modules.defectNotice.feedback && (
                <Button
                  onClick={() =>
                    navigate(
                      routePath.feedback.replace(
                        ':contractId',
                        currentContractId
                      ),
                      { state: { from: 'contact' } }
                    )
                  }
                  variant="secondary"
                  className="w-full"
                >
                  {t('contact:feedback.button')}
                </Button>
              )}
              {modules.defectNotice.question && (
                <Button
                  onClick={() =>
                    navigate(
                      routePath.question.replace(
                        ':contractId',
                        currentContractId
                      ),
                      { state: { from: 'contact' } }
                    )
                  }
                  variant="secondary"
                  className="w-full"
                >
                  {t('contact:question.button')}
                </Button>
              )}
            </div>
          )}

          {salesRepresentative && (
            <ContactBlock
              title={t('common:buildingInfo.sales')}
              contact={salesRepresentative}
              label={t('common:buildingInfo.sales')}
            />
          )}

          {residentsCommittee && residentsCommittee.length > 0 && (
            <div className="mb-8">
              <Heading element="h2" size="l" className="my-4">
                {t('contact:residentsCommittee')}
              </Heading>
              {residentsCommittee.map((member) => (
                <div key={member.id} className="mb-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-common-gray-6">
                        {member.serviceRole}
                      </p>
                    </div>
                    <div className="flex">
                      {member.phoneNumber && (
                        <RoundLink
                          className="flex items-center mr-3 min-tablet:hidden"
                          href={`tel:${member.phoneNumber}`}
                        >
                          <Icon icon="phone" size="lg" className="w-6" />
                        </RoundLink>
                      )}
                      {member.email && (
                        <RoundLink
                          className="flex items-center"
                          href={`mailto:${member.email}`}
                        >
                          <Icon icon="email" size="lg" className="w-6" />
                        </RoundLink>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(!contacts || contacts.length === 0) && (
            <div className="text-center py-8 text-common-gray-6">
              {t('common:buildingInfo.noContactInformation')}
            </div>
          )}
        </div>
      </View.Content>
    </View>
  )
}

const ContactBlock = ({
  title,
  contact,
  label,
  showName = true,
}: {
  title?: string
  contact: TContact
  label?: string
  showName?: boolean
}) => {
  const { t } = useTranslation(['contact'])

  return (
    <div className="mb-8">
      <div className="flex justify-between items-start">
        <div>
          {title && (
            <p className="text-base tablet:text-xl font-medium my-4">{title}</p>
          )}
          {showName && <p className="my-1">{contact.name}</p>}
          {contact.phoneNumber && (
            <p className="text-sm lg:text-base my-1">{contact.phoneNumber}</p>
          )}
          <p className="text-sm lg:text-base my-1 underline">
            <a href={`mailto:${contact.email}`}>{contact.email}</a>
          </p>
          {label && <p className="text-xs text-common-gray-6 mt-2">{label}</p>}
        </div>
        <div className="flex">
          {contact.phoneNumber && (
            <RoundLink
              className="flex items-center mr-3 min-tablet:hidden"
              href={`tel:${contact.phoneNumber}`}
            >
              <Icon
                icon="phone"
                size="lg"
                className="w-6"
                alt={`${t('contact:call')} ${contact.phoneNumber}`}
              />
            </RoundLink>
          )}
          <RoundLink
            className="flex items-center"
            href={`mailto:${contact.email}`}
          >
            <Icon
              icon="email"
              size="lg"
              className="w-6"
              alt={`${t('contact:email')} ${contact.email}`}
            />
          </RoundLink>
        </div>
      </div>
    </div>
  )
}

const resolveContacts = (contacts?: TContact[]) => {
  const propertyManager = contacts?.find(
    (c) => c.contactCategory === ContactCategory.PROPERTY_MANAGEMENT
  )
  const propertyMaintenance = contacts?.find(
    (c) => c.contactCategory === ContactCategory.PROPERTY_MAINTENANCE
  )
  const propertySecretaries = contacts?.filter(
    (c) => c.contactCategory === ContactCategory.PROPERTY_SECRETARY
  )
  const customerService = contacts?.find(
    (c) => c.contactCategory === ContactCategory.CUSTOMER_SERVICE
  )
  const salesRepresentative = contacts?.find(
    (c) => c.contactCategory === ContactCategory.SALES_REPRESENTATIVE
  )

  // Filter out members of the residents' committee
  const residentsCommittee = contacts?.filter(
    (c) => c.contactCategory === ContactCategory.RESIDENT_COMMITTEE
  )

  return {
    propertyManager,
    propertyMaintenance,
    propertySecretaries,
    customerService,
    salesRepresentative,
    residentsCommittee,
  }
}
